import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ChevronDown, ChevronRight, CircleCheck, CircleMinus, CircleX, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { checkSignalsAlgoIntradayAlgoSignalsAlgoCheckPost as checkSignalsAlgo } from "@/client-admin/services.gen";
import { LimitCheckResult, PTU_Identifier, TradingLimitCheck } from "@/client-admin/types.gen";
import { cn, getErrorDetails } from "@/lib/utils";
import { BreachList, measurementPeriod } from "./BreachList";
import { BREACH_UNITS, checkLabel, formatCheckValue, formatPeriod, mergeConsecutivePeriods } from "./tradingLimits";

interface EnableSignalsDialogProps {
  signalIds: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isConfirming: boolean;
}

/** Dry run of the trading limit checks before held signals are set to Algo 1.
 * Confirm is enabled only when every check passes; the backend re-checks on save. */
export function EnableSignalsDialog({
  signalIds,
  open,
  onOpenChange,
  onConfirm,
  isConfirming,
}: EnableSignalsDialogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["intraday-algo-limit-check", signalIds],
    queryFn: () => checkSignalsAlgo({ requestBody: { ids: signalIds } }),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !isConfirming && onOpenChange(nextOpen)}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Set {signalIds.length} signal(s) to Algo 1</AlertDialogTitle>
          <AlertDialogDescription>
            Trading limit checks on the live signals plus the selected ones, per delivery date. Each check shows its
            worst PTU, hour or day against the limit; click a failing check to list every breach.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{getErrorDetails(error)}</AlertDescription>
          </Alert>
        )}
        {data && <TradingLimitCheckTable check={data} />}
        {data && !data.passed && <NextSteps check={data} />}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>Cancel</AlertDialogCancel>
          <Button
            onClick={onConfirm}
            disabled={!data?.passed || isConfirming}
            title={data && !data.passed ? "Every check must pass before the signals can be enabled" : undefined}
          >
            {isConfirming ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TradingLimitCheckTable({ check }: { check: TradingLimitCheck }) {
  const deliveryDates = [
    ...new Set([
      ...check.results.map((result) => result.delivery_date),
      ...check.missing_day_ahead_prices.map((ptu) => ptu.delivery_date),
    ]),
  ].sort();

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Check</TableHead>
            <TableHead>Worst</TableHead>
            <TableHead className="text-right">Actual</TableHead>
            <TableHead className="text-right">Limit</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveryDates.map((deliveryDate) => (
            <DeliveryDateRows
              key={deliveryDate}
              deliveryDate={deliveryDate}
              results={check.results.filter((result) => result.delivery_date === deliveryDate)}
              missingDayAheadPrices={check.missing_day_ahead_prices.filter((ptu) => ptu.delivery_date === deliveryDate)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DeliveryDateRows({
  deliveryDate,
  results,
  missingDayAheadPrices,
}: {
  deliveryDate: string;
  results: LimitCheckResult[];
  missingDayAheadPrices: PTU_Identifier[];
}) {
  const missingPeriods = mergeConsecutivePeriods(
    missingDayAheadPrices.map((ptu) => ({ start: ptu.start_time, end: ptu.end_time })),
  );
  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={5} className="font-semibold">
          {deliveryDate}
        </TableCell>
      </TableRow>
      {results.map((result) => (
        <CheckRows key={result.limit_name} result={result} />
      ))}
      {missingPeriods.map((period) => (
        <TableRow key={period.start}>
          <TableCell>Day Ahead price missing</TableCell>
          <TableCell className="whitespace-nowrap">{formatPeriod(period.start, period.end)}</TableCell>
          <TableCell className="text-right">—</TableCell>
          <TableCell className="text-right">—</TableCell>
          <TableCell>
            <StatusLabel status="failed" text="Missing" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function CheckRows({ result }: { result: LimitCheckResult }) {
  const [expanded, setExpanded] = useState(false);
  const expandable = result.breaches.length > 0;
  return (
    <>
      <TableRow
        className={cn(expandable && "cursor-pointer")}
        onClick={expandable ? () => setExpanded(!expanded) : undefined}
        aria-expanded={expandable ? expanded : undefined}
      >
        <TableCell>{checkLabel(result.limit_name)}</TableCell>
        <TableCell className="whitespace-nowrap">{result.worst ? measurementPeriod(result.worst) : "—"}</TableCell>
        <TableCell className="whitespace-nowrap text-right tabular-nums">
          {result.worst ? formatCheckValue(result.limit_name, result.worst.actual) : "—"}
        </TableCell>
        <TableCell className="whitespace-nowrap text-right tabular-nums">
          {result.worst ? formatCheckValue(result.limit_name, result.worst.limit) : "—"}
        </TableCell>
        <TableCell>
          <span className="flex items-center gap-1">
            <CheckStatus result={result} />
            {expandable &&
              (expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              ))}
          </span>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="bg-red-50/50 px-4 py-2">
            <BreachList result={result} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CheckStatus({ result }: { result: LimitCheckResult }) {
  if (!result.enabled) return <StatusLabel status="off" text="Off" />;
  // An enabled price check measures nothing when every Day Ahead price is missing.
  if (!result.worst) return <StatusLabel status="failed" text="Not measured" />;
  const breachCount = result.breaches.length;
  if (breachCount === 0) return <StatusLabel status="passed" text="" />;
  if (breachCount === 1) return <StatusLabel status="failed" text="" />;
  return <StatusLabel status="failed" text={`${breachCount} ${BREACH_UNITS[result.limit_name].counted} over`} />;
}

const STATUS_STYLES = {
  passed: { Icon: CircleCheck, className: "text-green-700", title: "Within the limit" },
  failed: { Icon: CircleX, className: "text-red-700", title: "Blocks enabling the signals" },
  off: { Icon: CircleMinus, className: "text-muted-foreground", title: "Check turned off for this delivery date" },
};

function StatusLabel({ status, text }: { status: keyof typeof STATUS_STYLES; text: string }) {
  const { Icon, className, title } = STATUS_STYLES[status];
  return (
    <span className={cn("flex items-center gap-1 whitespace-nowrap", className)} title={title}>
      <Icon className="h-4 w-4" />
      {text}
    </span>
  );
}

function NextSteps({ check }: { check: TradingLimitCheck }) {
  const breachDates = [
    ...new Set(check.results.filter((result) => result.breaches.length > 0).map((result) => result.delivery_date)),
  ].sort();
  const missingPriceDates = [...new Set(check.missing_day_ahead_prices.map((ptu) => ptu.delivery_date))].sort();
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="space-y-1">
        {breachDates.length > 0 && (
          <p>Override the breached limits for {breachDates.join(", ")} in the Limits tab, then try again.</p>
        )}
        {missingPriceDates.length > 0 && (
          <p>
            Import the Day Ahead prices for {missingPriceDates.join(", ")}, or turn off both price delta checks for that
            date in the Limits tab, then try again.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}
